import { useAnalyticsInitialization } from "@faro-lotv/foreign-observers";
import { useManualPageViewTracking } from "@hooks/use-manual-page-view-tracking";
import { useAppParams } from "@router/router-helper";
import { runtimeConfig } from "@src/runtime-config";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import {
  selectedSdbCompanyIdSelector,
  selectedSdbCompanyNameSelector,
} from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { getAmplitudeKeys } from "@utils/amplitude-utils";
import { useMemo } from "react";

/**
 * Initializes Analytics module for tracking.
 */
export function useInitAppAnalytics(): void {
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const selectedSdbCompanyId = useAppSelector(selectedSdbCompanyIdSelector);
  const selectedProject = useAppSelector(selectedProjectSelector);
  const selectedSdbCompanyName = useAppSelector(selectedSdbCompanyNameSelector);
  const params = useAppParams();

  /** Selects which data should be privatized not to be sent to Amplitude */
  const privateData = useMemo(
    () => ({
      projectName: selectedProject?.name,
      companyName: selectedSdbCompanyName ?? undefined,
      userFirstName: loggedInUser?.firstName,
      userLastName: loggedInUser?.lastName,
      userEmail: loggedInUser?.email,
    }),
    [selectedProject, selectedSdbCompanyName, loggedInUser]
  );

  const amplitudeKeys = getAmplitudeKeys({
    loggedInUser,
  });

  useManualPageViewTracking();

  useAnalyticsInitialization({
    app: "XG Dashboard",
    appVersion: runtimeConfig.appVersion,
    keys: amplitudeKeys,
    currentUserEmail: loggedInUser?.email,
    companyId: selectedSdbCompanyId ?? params.companyId,
    projectId: selectedProject?.id ?? params.projectId,
    userId: loggedInUser?.id,
    region: runtimeConfig.appRegion,
    environment: runtimeConfig.environment,
    privateData,
    /* eslint-disable @typescript-eslint/naming-convention -- Library name */

    // Set it to false, since we already use the amplitude keys to distinguish internal users
    ignoreInternalUsers: false,

    // Set it to false, since we already use the amplitude keys to distinguish anonymous users
    ignoreAnonymousUsers: true,

    // Set it to false, to avoid any automatic tracking by Amplitude
    defaultTracking: false,
    /* eslint-enable @typescript-eslint/naming-convention */
  });
}
